import { useMutation, useQuery } from "@tanstack/react-query";
import { executeQuery, REACT_APP_LEARNER_LINK } from "../../../utils";
import { LearnerLicense } from "../../../manager/components/subscriptions/vouchers/types";
import { DmLoadingSpinner } from "../../../manager/utils/functions";
import { InviteError } from "./InviteError";
import { useNavigate } from "react-router-dom";
import { addMonths, format } from "date-fns";
import Button from "../../../student/components/generic/button";
import { useState } from "react";
import { Learner } from "../../types";
import { InviteSupportLink } from "./InviteSupportLink";

function getCurrentExpirationDate(): Date {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const learner: Learner = user && user.entitlements ? user : null;

  const expirationDate = new Date(learner?.subscriptionExpiration || 0);
  const currentDate = new Date();

  return currentDate.getTime() > expirationDate.getTime()
    ? currentDate
    : expirationDate;
}

const EligibilityError: React.FC<{ reason: string }> = ({ reason }) => {
  switch (reason) {
    case "license_used":
      return (
        <p>
          This license has already been accepted. Please email us at{" "}
          <InviteSupportLink /> if you need further assistance.
        </p>
      );
    case "license_remaining":
      return (
        <p>
          You have more than two months remaining on your current license.
          Please try again when your license is closer to expiring. Email us at{" "}
          <InviteSupportLink /> if you need further assistance.
        </p>
      );
    case "invalid_quantity":
    case "billing_entitlement":
    case "previous_subscription":
    default:
      return (
        <p>
          There was an unexpected problem with this license. Please email us at{" "}
          <InviteSupportLink /> for more information.
        </p>
      );
  }
};

export const InviteAccept: React.FC<{ license: LearnerLicense }> = ({
  license,
}) => {
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery<
    { eligible: true } | { eligible: false; reason: string }
  >({
    queryKey: ["checkLicenseEligibility"],
    queryFn: () => {
      setAcceptError(null);
      return executeQuery({
        path: "/learner/shared/license/eligibility",
        params: { licenseId: license._id },
      });
    },
    refetchOnWindowFocus: false,
  });

  const { mutate, isPending: isAcceptingLicense } = useMutation({
    mutationFn: () =>
      executeQuery({
        path: "/learner/shared/license/accept",
        method: "post",
        requestBody: { licenseId: license._id },
      }),
  });

  const acceptLicense = () => {
    mutate(undefined, {
      onSuccess: (
        result: { success: false } | { success: true; learner: Learner }
      ) => {
        if (result.success) {
          localStorage.setItem("user", JSON.stringify(result.learner));
          if (result.learner.entitlements.includes("parent")) {
            navigate(
              (result.learner.child_ids?.length ?? 0) ===
                result.learner.subscriptionQuantity
                ? `${REACT_APP_LEARNER_LINK}/parent/profile`
                : `${REACT_APP_LEARNER_LINK}/parent/learners/start`
            );
          } else {
            navigate(`${REACT_APP_LEARNER_LINK}/dashboard`);
          }
        } else {
          setAcceptError("You are unable to accept this license.");
        }
      },
      onError: () => {
        setAcceptError("There was an error accepting the license.");
      },
    });
  };

  const nextExpirationDate = addMonths(
    getCurrentExpirationDate(),
    license.licenseLength
  );
  const formattedExpirationDate = format(nextExpirationDate, "MMMM d, yyyy");

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <DmLoadingSpinner message="" />
      </div>
    );
  }

  if (error) {
    return <InviteError>There was an error checking eligibility.</InviteError>;
  }

  if (data && !data.eligible) {
    return (
      <InviteError>
        <EligibilityError reason={data.reason} />
      </InviteError>
    );
  }

  if (data && data.eligible) {
    return (
      <>
        <div className="flex flex-col gap-2 text-center">
          <h1 className="font-serif text-2xl font-bold text-dm-brand-blue-600">
            Accept Your License
          </h1>
          <p className="font-serif">
            You&apos;ll be able to access DeltaMath for Home until{" "}
            <strong>{formattedExpirationDate}</strong>.
          </p>
        </div>

        <Button
          className="mt-6 w-full max-w-md"
          onClick={acceptLicense}
          isLoading={isAcceptingLicense}
        >
          Accept License
        </Button>

        {acceptError && (
          <p className="-mt-6 text-center text-sm text-dm-error-500">
            {acceptError}
          </p>
        )}
      </>
    );
  }

  return <></>;
};
